/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker } from "@mui/x-date-pickers";
import { styled } from '@mui/material/styles';
import SurfaceSection from "../../Common/SurfaceSection";
import dayjs from "dayjs";
import ReportsPieChart from "./ReportPieChart";
import { ClientTypesDescriptionTranslationKeys, ClientTypesTranslationKeys } from "../../../constants/reports/ClientTypes";
import { ClientType } from "../../../types/Reports";
import useReportsService from "../../../services/useReportsService";
import { formatNumber } from "../../../utils/formatNumber";
import { setStartOfNextDay } from "../../../utils/time";

const CloseButton = styled(IconButton)(({ theme }) => `
    color: ${theme.palette.primary[100]};
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
`);

const DateDivider = styled('div')(({ theme }) => `
    background-color: ${theme.palette.text.secondary};
    height: 1px;
    margin: 0 ${theme.spacing(1)};
    width: ${theme.spacing(1.5)};
`);

const DescriptionText = styled('span')(({ theme }) => `
    color: ${theme.palette.text.secondary};
    display: block;
    font-size: 0.95em;
    white-space: nowrap;
`);

const DefaultFilters = {
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
}

const formatDate = (date: dayjs.Dayjs | null) => {
    return date ? date.format("YYYY-MM-DD") : "";
};

function ClientTypesReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [clientTypesReport, setClientTypesReport] = useState<ClientType[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pieWidth, setPieWidth] = useState(400);
    const [startDate, setStartDate] = useState<string>(DefaultFilters.startDate);
    const [endDate, setEndDate] = useState<string>(DefaultFilters.endDate);
    const pieParentRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation("common");
    const { fetchClientTypesReportData } = useReportsService();

    useEffect(() => {
        if (!open) return;

        setIsLoading(true);
        fetchClientTypesReportData(
            new Date(startDate + "T00:00").getTime(),
            setStartOfNextDay(endDate + "T00:00").getTime(),
        ).then((data) => {
            setClientTypesReport(data);
            setError(null);
        }).catch(() => {
            setError(t("reports.common.errorFetchingData"));
        }).finally(() => {
            setIsLoading(false);
        });
    }, [startDate, endDate, open, fetchClientTypesReportData, t]);

    // adjust pie width based on parent container
    useEffect(() => {
        const handleResize = () => {
            if (pieParentRef.current) {
                const parentWidth = pieParentRef.current.offsetWidth;
                if (isTabletOrMobile) {
                    setPieWidth(parentWidth - 40);
                } else {
                    setPieWidth((parentWidth - 170) / 2);
                }
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [pieParentRef.current?.offsetWidth, pieParentRef.current?.offsetHeight, isTabletOrMobile]);

    // cleanup on close
    useEffect(() => {
        return () => {
            setStartDate(DefaultFilters.startDate);
            setEndDate(DefaultFilters.endDate);
            setPieWidth(400);
        };
    }, []);

    // prepare table data
    const tableData = useMemo(
        () =>
            (clientTypesReport && clientTypesReport.length > 0)
                ? clientTypesReport.map(c => ({
                    label: t(`${ClientTypesTranslationKeys[c.name]}.short`),
                    description: t(ClientTypesDescriptionTranslationKeys[c.name]),
                    uniqueClients: formatNumber(c.customers || 0),
                    totalVisits: formatNumber(c.entries || 0)
                }))
                : [],
        [clientTypesReport, t]
    );

    // prepare chart data
    const totalClients = useMemo(
        () => (clientTypesReport || []).reduce((acc, curr) => acc + (curr.customers || 0), 0),
        [clientTypesReport]
    );

    const totalVisits = useMemo(
        () => (clientTypesReport || []).reduce((acc, curr) => acc + (curr.entries || 0), 0),
        [clientTypesReport]
    );

    const uniqueClientsChartData = useMemo(
        () =>
            (clientTypesReport && clientTypesReport.length > 0)
                ? clientTypesReport.map(c => ({
                    label: c.name,
                    value: c.customers || 0
                }))
                : [],
        [clientTypesReport]
    );

    const totalVisitsChartData = useMemo(
        () =>
            (clientTypesReport && clientTypesReport.length > 0)
                ? clientTypesReport.map(c => ({
                    label: c.name,
                    value: c.entries || 0
                }))
                : [],
        [clientTypesReport]
    );
    // end prepare chart data

    const noData = useMemo(() => {
        return !totalClients && !totalVisits;
    }, [totalClients, totalVisits]);


    const renderContent = () => {
        if (error) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%" color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                </Box>
            );
        }

        if (noData) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%">
                        {t("reports.clientTypes.no-data")}
                    </Typography>
                </Box>
            );
        }
        return (
            <ReportsPieChart
                pieWidth={pieWidth}
                data={{
                    uniqueClientsChartData: {
                        data: uniqueClientsChartData,
                        totalValue: totalClients,
                        title: t("reports.clientTypes.uniqueClients.title"),
                    },
                    totalVisitsChartData: {
                        data: totalVisitsChartData,
                        totalValue: totalVisits,
                        title: t("reports.clientTypes.totalVisits.title"),
                    }
                }
                }
            />
        )
    };

    const renderTable = () => {
        if (isLoading || noData) return null;
        return (
            <Box>
                <Table size="small" sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.clientTypes.table.client-type")}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>{t("reports.clientTypes.table.nr-unique-clients")}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>{t("reports.clientTypes.table.nr-total-visits")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.map((row) => (
                            <TableRow key={row.label}>
                                <TableCell>
                                    <span>{row.label}</span>
                                    <DescriptionText>{row.description}</DescriptionText>
                                </TableCell>
                                <TableCell align="center">{row.uniqueClients}</TableCell>
                                <TableCell align="center">{row.totalVisits}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        )
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                {t("reports.clientTypes.title")}
                <CloseButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}>
                <Box sx={{ mb: 2 }}>
                    <Typography>
                        {t("reports.clientTypes.subtitle")}
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    flex={1}
                    gap={2}
                    flexDirection={isTabletOrMobile ? "column" : "row"}
                >
                    <Box
                        flex={isTabletOrMobile ? 12 : 3}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        <Box alignItems="center" display="flex">
                            <DatePicker
                                label={t("search.filters.from")}
                                value={dayjs(startDate)}
                                onChange={(ev) => setStartDate(formatDate(ev))}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                    },
                                }}
                            />
                            <DateDivider />
                            <DatePicker
                                label={t("search.filters.to")}
                                value={dayjs(endDate)}
                                onChange={(ev) => setEndDate(formatDate(ev))}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                    },
                                }}
                            />
                        </Box>
                        {!isTabletOrMobile && renderTable()}
                    </Box>

                    <SurfaceSection
                        ref={pieParentRef}
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flex: isTabletOrMobile ? 12 : 9,
                            flexDirection: isTabletOrMobile ? "column" : "row",
                            justifyContent: "center",
                            pb: isTabletOrMobile ? 2 : 0,
                        }}
                    >
                        {renderContent()}
                    </SurfaceSection>
                    {isTabletOrMobile && renderTable()}
                </Box>
            </DialogContent >
        </Dialog >
    );
};

export default ClientTypesReportDialog;
